/**
 * Data Binding Engine
 * Manages data sources and field binding resolution for dynamic form behavior
 */

import type { FormSchema, FormControl, FieldBinding } from '@atlas-forms/types-js';
import { getAtlasLogger } from '@atlas-forms/types-js';
import { ExpressionEvaluator, type EvaluationContext, type EvaluatorFunction } from './ExpressionEvaluator';

/**
 * Minimal HTTP client interface required for API data source resolution.
 * Implement this interface and pass an instance to DataBindingEngine's constructor
 * to enable `type: 'api'` data sources. Compatible with BaseHttpClient from api-client-js.
 */
export interface IDataSourceHttpClient {
  get<T = unknown>(
    endpoint: string,
    options?: { timeout?: number }
  ): Promise<{ success: boolean; data?: T; errorMessage?: string }>;

  post<T = unknown>(
    endpoint: string,
    data: unknown,
    options?: { timeout?: number }
  ): Promise<{ success: boolean; data?: T; errorMessage?: string }>;
}

/**
 * Data source types
 */
export type DataSourceType = 'json' | 'api' | 'variable' | 'form' | 'computed';

/**
 * Data source configuration
 */
export interface DataSource {
  /** Unique identifier for this data source */
  id: string;

  /** Type of data source */
  type: DataSourceType;

  /** Path to data (e.g., 'customer.profile', '/api/users/{id}', 'myVariable') */
  path: string;

  /** Whether to cache the result */
  cache?: boolean;

  /** Cache time-to-live in milliseconds */
  ttl?: number;

  /** URL parameters for API sources (e.g., { id: 123 }) */
  params?: Record<string, any>;

  /** API method (GET, POST, etc.) */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

  /** Request body for API sources */
  body?: Record<string, any>;

  /** Custom headers for API calls */
  headers?: Record<string, string>;

  /** Fallback value if data source fails */
  fallback?: any;

  /** Description of this data source */
  description?: string;
}

/**
 * Binding resolution result
 */
export interface BindingResolutionResult {
  /** Whether the binding was successfully resolved */
  success: boolean;

  /** Resolved value */
  value: any;

  /** Source of the value (e.g., 'form', 'api', 'variable') */
  source?: string;

  /** Any error that occurred during resolution */
  error?: string;

  /** Whether this is a cached result */
  cached?: boolean;
}

/**
 * Field binding with resolved value
 */
export interface ResolvedBinding {
  /** Field ID that was bound */
  fieldId: string;

  /** Field binding configuration */
  binding: FieldBinding;

  /** Resolved value */
  value: any;

  /** Whether resolution was successful */
  success: boolean;

  /** Any error that occurred */
  error?: string;
}

/**
 * Cache entry
 */
interface CacheEntry {
  value: any;
  timestamp: number;
  ttl: number;
}

/**
 * Data Binding Engine - Manages data sources and binding resolution
 */
export class DataBindingEngine {
  private dataSources: Map<string, DataSource>;
  private cache: Map<string, CacheEntry>;
  private dataCache: Map<string, any>;
  private evaluationContext: EvaluationContext;
  private httpClient: IDataSourceHttpClient | null;

  constructor(httpClient?: IDataSourceHttpClient) {
    this.dataSources = new Map();
    this.cache = new Map();
    this.dataCache = new Map();
    this.httpClient = httpClient ?? null;
    this.evaluationContext = {
      values: {},
      data: {},
      functions: {},
      variables: {},
    };
  }

  /**
   * Register a data source
   */
  registerDataSource(source: DataSource): void {
    this.dataSources.set(source.id, source);
  }

  /**
   * Unregister a data source
   */
  unregisterDataSource(sourceId: string): void {
    this.dataSources.delete(sourceId);
    // Clear cache for this source
    for (const [key] of this.cache) {
      if (key.startsWith(`${sourceId}:`)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get a registered data source
   */
  getDataSource(sourceId: string): DataSource | undefined {
    return this.dataSources.get(sourceId);
  }

  /**
   * Set form values
   */
  setFormValues(values: Record<string, any>): void {
    this.evaluationContext.values = { ...values };
  }

  /**
   * Set external data
   */
  setData(data: Record<string, any>): void {
    this.evaluationContext.data = { ...data };
  }

  /**
   * Set variables
   */
  setVariables(variables: Record<string, any>): void {
    this.evaluationContext.variables = { ...variables };
  }

  /**
   * Register a custom function
   */
  registerFunction(name: string, fn: EvaluatorFunction): void {
    if (!this.evaluationContext.functions) {
      this.evaluationContext.functions = {};
    }
    this.evaluationContext.functions[name] = fn;
  }

  /**
   * Resolve a field binding
   */
  async resolveBinding(binding: FieldBinding): Promise<BindingResolutionResult> {
    try {
      // Try expression first (highest priority)
      if (binding.expression) {
        const value = ExpressionEvaluator.evaluate(binding.expression, this.evaluationContext);
        return {
          success: true,
          value,
          source: 'expression',
        };
      }

      // Try source + path
      if (binding.source) {
        const result = await this.resolveDataSource(binding.source, binding.path);
        return result;
      }

      // Try just path (form field reference)
      if (binding.path) {
        const value = ExpressionEvaluator.evaluateVariable(binding.path, this.evaluationContext);
        return {
          success: true,
          value,
          source: 'form',
        };
      }

      return {
        success: false,
        value: null,
        error: 'No binding source or expression provided',
      };
    } catch (error) {
      return {
        success: false,
        value: null,
        error: String(error),
      };
    }
  }

  /**
   * Resolve a data source
   */
  private async resolveDataSource(sourceId: string, path?: string): Promise<BindingResolutionResult> {
    const source = this.dataSources.get(sourceId);
    if (!source) {
      return {
        success: false,
        value: null,
        error: `Data source '${sourceId}' not found`,
      };
    }

    // Check cache first
    const cacheKey = `${sourceId}:${path || ''}`;
    if (source.cache && this.cache.has(cacheKey)) {
      const entry = this.cache.get(cacheKey)!;
      if (Date.now() - entry.timestamp < entry.ttl) {
        return {
          success: true,
          value: entry.value,
          source: sourceId,
          cached: true,
        };
      }
      this.cache.delete(cacheKey);
    }

    let value: any;
    let resolvedSource = sourceId;

    try {
      switch (source.type) {
        case 'json':
          value = this.resolveJsonPath(source.path, path);
          break;

        case 'variable':
          value = this.evaluationContext.variables?.[source.path];
          if (path) {
            value = this.resolvePath(value, path);
          }
          break;

        case 'form':
          value = this.evaluationContext.values[source.path];
          if (path) {
            value = this.resolvePath(value, path);
          }
          break;

        case 'api':
          value = await this.resolveApiDataSource(source, path);
          break;

        case 'computed':
          // For computed sources, use the path as an expression
          value = ExpressionEvaluator.evaluate(source.path || path || '', this.evaluationContext);
          break;

        default:
          return {
            success: false,
            value: source.fallback || null,
            error: `Unknown data source type: ${source.type}`,
          };
      }

      // Cache result
      if (source.cache) {
        this.cache.set(cacheKey, {
          value,
          timestamp: Date.now(),
          ttl: source.ttl || 60000, // Default 1 minute
        });
      }

      return {
        success: value !== null && value !== undefined,
        value: value ?? source.fallback,
        source: resolvedSource,
      };
    } catch (error) {
      return {
        success: false,
        value: source.fallback || null,
        error: String(error),
      };
    }
  }

  /**
   * Resolve JSON data source
   */
  private resolveJsonPath(basePath: string, subPath?: string): any {
    const data = this.dataCache.get(basePath) ||
                 this.evaluationContext.data?.[basePath] ||
                 this.evaluationContext.variables?.[basePath];

    if (subPath) {
      return this.resolvePath(data, subPath);
    }

    return data;
  }

  /**
   * Resolve a dot-notation path in an object
   */
  private resolvePath(obj: any, path?: string): any {
    if (!path || !obj) return obj;

    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current === null || current === undefined) {
        return null;
      }
      current = current[part];
    }

    return current;
  }

  /**
   * Resolve API data source via the configured HTTP client.
   * Requires an IDataSourceHttpClient to be passed to the constructor.
   * Supports GET, POST, PUT, PATCH, DELETE — defaults to GET.
   * Uses source.params for URL interpolation and source.body for request body.
   */
  private async resolveApiDataSource(source: DataSource, subPath?: string): Promise<any> {
    if (!this.httpClient) {
      throw new Error(
        `API data source '${source.id}' requires an HTTP client. ` +
        `Pass an IDataSourceHttpClient instance to the DataBindingEngine constructor.`
      );
    }

    // Interpolate URL parameters into the endpoint path
    let endpoint = source.path;
    if (source.params) {
      for (const [key, value] of Object.entries(source.params)) {
        endpoint = endpoint.replace(`{${key}}`, String(value));
      }
    }

    const method = source.method ?? 'GET';
    let response: { success: boolean; data?: unknown; errorMessage?: string };

    if (method === 'GET' || method === 'DELETE') {
      response = await this.httpClient.get(endpoint);
    } else {
      // POST, PUT, PATCH — send body
      response = await this.httpClient.post(endpoint, source.body ?? {});
    }

    if (!response.success) {
      throw new Error(
        `API data source '${source.id}' request to '${endpoint}' failed: ` +
        (response.errorMessage ?? 'Unknown error')
      );
    }

    const data = response.data ?? source.fallback ?? null;
    return subPath ? this.resolvePath(data, subPath) : data;
  }

  /**
   * Resolve all bindings in a form schema with timeout protection
   * @param schema Form schema containing controls with bindings
   * @param timeoutMs Timeout in milliseconds (default: 30000)
   * @returns Array of resolved bindings, with partial results if timeout occurs
   */
  async resolveAllBindings(schema: FormSchema, timeoutMs: number = 30000): Promise<ResolvedBinding[]> {
    const results: ResolvedBinding[] = [];
    const controls = this.getAllControls(schema);

    // Create timeout promise that rejects after specified time
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(() => {
        reject(new Error(`Binding resolution timeout after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    try {
      // Process all controls with per-control timeout handling
      const promises = controls.map(async (control) => {
        try {
          const resultObj: ResolvedBinding[] = [];

          // Resolve binding with timeout
          if (control.binding) {
            try {
              const resolution = await Promise.race([
                this.resolveBinding(control.binding),
                timeoutPromise,
              ]);
              resultObj.push({
                fieldId: control.id,
                binding: control.binding,
                value: resolution.value,
                success: resolution.success,
                error: resolution.error,
              });
            } catch (error) {
              if (error instanceof Error && error.message.includes('timeout')) {
                // Timeout on this binding, record as error
                resultObj.push({
                  fieldId: control.id,
                  binding: control.binding,
                  value: undefined,
                  success: false,
                  error: `Binding resolution timeout: ${(error as Error).message}`,
                });
              } else {
                // Other error during resolution
                resultObj.push({
                  fieldId: control.id,
                  binding: control.binding,
                  value: undefined,
                  success: false,
                  error: String(error),
                });
              }
            }
          }

          // Resolve default value expressions (synchronous, no timeout needed)
          if (control.defaultValue && typeof control.defaultValue === 'string') {
            const cleaned = control.defaultValue.trim();
            if (cleaned.startsWith('{{') && cleaned.endsWith('}}')) {
              try {
                const value = ExpressionEvaluator.evaluate(cleaned, this.evaluationContext);
                resultObj.push({
                  fieldId: control.id,
                  binding: { expression: cleaned },
                  value,
                  success: true,
                });
              } catch (error) {
                resultObj.push({
                  fieldId: control.id,
                  binding: { expression: cleaned },
                  value: undefined,
                  success: false,
                  error: String(error),
                });
              }
            }
          }

          return resultObj;
        } catch (error) {
          // Catch unexpected errors for this control
          getAtlasLogger().warn(`[DataBindingEngine] Unexpected error resolving bindings for control ${control.id}`, error);
          return [];
        }
      });

      // Execute all promises and collect results
      const allResults = await Promise.all(promises);
      for (const controlResults of allResults) {
        results.push(...controlResults);
      }

      return results;
    } catch (error) {
      // Global timeout occurred - return partial results
      if (error instanceof Error && error.message.includes('timeout')) {
        getAtlasLogger().warn(`[DataBindingEngine] Global binding resolution timeout after ${timeoutMs}ms. Returning ${results.length} resolved bindings.`);
        return results;
      }

      // Unexpected error during resolution
      getAtlasLogger().error('[DataBindingEngine] Error during binding resolution', error);
      return results;
    } finally {
      // Always clear timeout to prevent memory leak
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  /**
   * Get all controls from a schema (flat list)
   */
  private getAllControls(schema: FormSchema): FormControl[] {
    const controls: FormControl[] = [];

    if (schema.controls) {
      controls.push(...schema.controls);
    }

    return controls;
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cache.clear();
    this.dataCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    entries: { key: string; cached: boolean }[];
  } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()).map(key => ({
        key,
        cached: true,
      })),
    };
  }

  /**
   * Get current evaluation context
   */
  getContext(): EvaluationContext {
    return this.evaluationContext;
  }

  /**
   * Update evaluation context completely
   */
  setContext(context: EvaluationContext): void {
    this.evaluationContext = context;
  }
}
